import React from 'react';
import styled from 'styled-components';

import { days } from 'src/firebase/db/Shops';

import StyleConfig from 'src/constants/StyleConfig';
import { HalfHourOptions } from 'src/constants/BusinessTime';

import InputLabelHolder from 'src/components/molecules/form/InputLabelHolder';
import SelectBox from 'src/components/atoms/form/SelectBox';
import AddButton from 'src/components/atoms/button/AddButton';

export type BusinessDayMarkerProps = {
  businessTimes: BusinessTime[];
  holidays: string[];
  onChange: (times: BusinessTime[]) => void;
};
export type HolidayMarkerProps = {
  holidays: string[];
  businessDays: string[];
  onChange: (holidays: string[]) => void;
};

export type BusinessTime = {
  days: string[];
  openingTime: string;
  closingTime: string;
};

type DayButtonProps = {
  day: BusinessTime['days'][number];
  isSelected: boolean;
  isDisabled: boolean;
  onChange: () => void;
};
type DaySelectorProps = {
  selectedDays: string[];
  isDisabledDays: string[];
  onChange: (days: string[]) => void;
};
type TimeSelectorProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
};
type BusinessDayRowProps = BusinessTime & {
  isDisabledDays: string[];
  onChange: (value: Partial<BusinessTime>) => void;
};
type BusinessDayRowAddbuttonProps = {
  onClick: () => void;
};

const DayButton = (props: DayButtonProps) => {
  return (
    <DayLabel aria-checked={props.isSelected} aria-disabled={props.isDisabled}>
      <input
        type="checkbox"
        name="days"
        value={props.day}
        disabled={props.isDisabled}
        onChange={props.onChange}
      />
      {getDayLabelJp((props.day as unknown) as typeof days[number])}
    </DayLabel>
  );
};

const DaySelector = (props: DaySelectorProps) => {
  const { selectedDays, onChange, isDisabledDays } = props;
  return (
    <InputBlock>
      {days.map((day) => {
        const isSelected = selectedDays.includes(day);
        const isDisabled = !isSelected && isDisabledDays.includes(day);
        const handleChange = () => {
          onChange(isSelected ? selectedDays.filter((d) => d !== day) : [...selectedDays, day]);
        };
        return (
          <DayButton
            day={day}
            isSelected={isSelected}
            isDisabled={isDisabled}
            onChange={handleChange}
            key={day}
          />
        );
      })}
    </InputBlock>
  );
};

const TimeSelector = (props: TimeSelectorProps) => {
  return (
    <InputDateBlock>
      <InputDateLabel>{props.label}</InputDateLabel>
      <SelectBoxWrapper>
        <SelectBox
          id={props.id}
          options={HalfHourOptions}
          inputValue={props.value}
          onChange={props.onChange}
        />
      </SelectBoxWrapper>
    </InputDateBlock>
  );
};

const BusinessDayRow = (props: BusinessDayRowProps) => {
  const { days, openingTime, closingTime, onChange, isDisabledDays } = props;
  return (
    <>
      <Lead>営業時間を入力する曜日を選択してください（複数選択可）</Lead>
      <DaySelector
        selectedDays={days}
        isDisabledDays={isDisabledDays}
        onChange={(value) => onChange({ days: value })}
      />
      <InputDateWrapper>
        <TimeSelector
          label="開店"
          id="opening"
          value={openingTime}
          onChange={(value) => onChange({ openingTime: value })}
        />
        <TimeSelector
          label="閉店"
          id="closing"
          value={closingTime}
          onChange={(value) => onChange({ closingTime: value })}
        />
      </InputDateWrapper>
    </>
  );
};

const BusinessDayRowAddbutton = (props: BusinessDayRowAddbuttonProps) => {
  return (
    <ButtonWrapper>
      <AddButton isDisabled={false} text="営業日を追加する" onClick={props.onClick} />
    </ButtonWrapper>
  );
};

const BusinessDayMarker = (props: BusinessDayMarkerProps) => {
  const isDisabledDays = [...props.holidays, ...props.businessTimes.map(({ days }) => days).flat()];
  const isSelectedAllDays = isDisabledDays.length === days.length;

  const addBusinessTime = () => {
    props.onChange([
      ...props.businessTimes,
      {
        days: [],
        openingTime: '',
        closingTime: '',
      },
    ]);
  };

  const curriedHandleUpdate = (index: number) => (payload: Partial<BusinessTime>) => {
    props.onChange(
      props.businessTimes.map((v, i) =>
        i === index ? { ...props.businessTimes[i], ...payload } : v,
      ),
    );
  };
  const curriedHandleDelete = (index: number) => () => {
    props.onChange(props.businessTimes.filter((_, i) => i !== index));
  };

  return (
    <Container>
      <InputLabelHolder labelName="営業時間" isRequired={true} />
      <div>
        {props.businessTimes.map((businessTime, index) => {
          return (
            <BusinessDayRowWrapper key={index}>
              <BusinessDayRow
                {...businessTime}
                isDisabledDays={isDisabledDays}
                onChange={curriedHandleUpdate(index)}
              />
              {index > 0 ? (
                <DeleteButton onClick={curriedHandleDelete(index)}>削除する</DeleteButton>
              ) : null}
            </BusinessDayRowWrapper>
          );
        })}
      </div>
      {isSelectedAllDays ? null : <BusinessDayRowAddbutton onClick={addBusinessTime} />}
    </Container>
  );
};

export const HolidayMarker = (props: HolidayMarkerProps) => {
  const { holidays, businessDays, onChange } = props;
  return (
    <Container>
      <InputLabelHolder labelName="定休日" isRequired={false} />
      <Lead>定休日の曜日を選択してください（複数選択可）</Lead>
      <DaySelector
        selectedDays={holidays}
        isDisabledDays={businessDays}
        onChange={(value) => onChange(value)}
      />
    </Container>
  );
};

export const getDayLabelJp = (label: typeof days[number]) => {
  switch (label) {
    case 'mon':
      return '月';
    case 'tue':
      return '火';
    case 'wed':
      return '水';
    case 'thu':
      return '木';
    case 'fri':
      return '金';
    case 'sat':
      return '土';
    case 'sun':
      return '日';
    case 'hol':
      return '祝日';
  }
};

const Container = styled.div`
  box-sizing: border-box;
`;

const Lead = styled.span`
  font-size: 12px;
  color: ${StyleConfig.COLOR.PRIMARY};
  margin: ${StyleConfig.HELPER.M8} auto;
`;

const InputBlock = styled.div`
  display: flex;
  justify-content: space-around;
  margin: ${StyleConfig.HELPER.M8} auto ${StyleConfig.HELPER.M16};
`;

const InputDateWrapper = styled.div`
  display: flex;
  justify-content: space-around;
`;

const InputDateBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48%;
`;

const InputDateLabel = styled.span`
  font-size: 12px;
  color: ${StyleConfig.COLOR.PRIMARY};
  margin-right: ${StyleConfig.HELPER.M8};
`;

const SelectBoxWrapper = styled.span`
  flex: 1;
`;

const ButtonWrapper = styled.div`
  margin: ${StyleConfig.HELPER.M28} auto ${StyleConfig.HELPER.M32};
  cursor: pointer;
`;

const DayLabel = styled.label`
  cursor: ${(props) => (props['aria-disabled'] ? 'not-allowed' : 'pointer')};
  border-radius: 50%;
  border-width: 2px;
  border-style: solid;
  border-color: ${(props) =>
    props['aria-disabled'] === true ? StyleConfig.COLOR.INPUT_BORDER : StyleConfig.COLOR.PRIMARY};
  background: ${(props) => () => {
    if (props['aria-disabled']) {
      return StyleConfig.COLOR.INPUT_BORDER;
    } else if (props['aria-checked']) {
      return StyleConfig.COLOR.PRIMARY;
    } else {
      return '#fff';
    }
  }};
  color: ${(props) =>
    props['aria-disabled'] || props['aria-checked'] ? '#fff' : StyleConfig.COLOR.PRIMARY};
  padding: 10px;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  input {
    appearance: none;
  }
`;

const BusinessDayRowWrapper = styled.div`
  &:not(:first-child) {
    margin-top: 30px;
  }
`;

const DeleteButton = styled.button`
  font-size: 12px;
  color: ${StyleConfig.COLOR.SECONDORY};
  float: right;
  margin-top: 12px;
  &:after {
    content: '';
    display: block;
    clear: both;
  }
`;

export default BusinessDayMarker;
